import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import type { GridApi, GridReadyEvent } from "ag-grid-community";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { useNavigate, useParams } from "react-router-dom";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	useGetAxiomData,
	useMonthlyEstimation,
	useUpdateMonthlyData,
} from "../hooks/useForecastDataEntry";
import type { MonthlyEstimation } from "../models/MonthlyEstimation";
import { ForecastDataEntryToolbar } from "./ForecastDataEntryToolbar";
import { ForecastDataEntryTable } from "./ForecastDataEntryTable";
import { ForecastDetailViewer } from "./ForecastDetailViewer";
import { ForecastChart } from "./ForecastChart";

const URL_SCHEMA = {
	// The year stays in the path, where the legacy menu links point. Only the selected
	// month travels in the query string.
	month: urlParam.string(),
};

const OUTER_LAYOUT = { left: 35, right: 65 };
const INNER_LAYOUT = { table: 60, detail: 40 };

export const ForecastDataEntryManager = () => {
	const { year: yearParam } = useParams<{ year?: string }>();
	const navigate = useNavigate();

	const basePath = "/production/forecast-data-entry";

	const initialYear = useMemo(() => {
		const parsed = yearParam ? Number(yearParam) : Number.NaN;
		return Number.isFinite(parsed) && parsed > 1900 && parsed < 2200
			? parsed
			: new Date().getFullYear();
	}, [yearParam]);

	const [year, setYear] = useState<number>(initialYear);
	const [{ month: selectedMonth }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const [notification, setNotification] = useState("");
	const gridApiRef = useRef<GridApi<MonthlyEstimation> | null>(null);

	useEffect(() => {
		setYear(initialYear);
	}, [initialYear]);

	const outerLayout = useDefaultLayout({
		id: "forecast-data-entry-outer-v2",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const innerLayout = useDefaultLayout({
		id: "forecast-data-entry-inner-v2",
		panelIds: ["table", "detail"],
		storage: localStorage,
	});

	const dataQuery = useMonthlyEstimation({ year });
	const rows = useMemo(() => dataQuery.data ?? [], [dataQuery.data]);

	// Guarded on the query having answered: while the rows are still empty every month,
	// including one restored from a link, would look gone.
	useEffect(() => {
		if (!dataQuery.isSuccess || !selectedMonth) return;
		if (!rows.some((r) => r.Month === selectedMonth)) dropValues("month");
	}, [dataQuery.isSuccess, rows, selectedMonth, dropValues]);

	const selectedRow = useMemo(
		() => rows.find((r) => r.Month === selectedMonth) ?? null,
		[rows, selectedMonth],
	);

	const { updateMonthlyData, isUpdating } = useUpdateMonthlyData({
		onSuccess: () => setNotification("Saved"),
		onError: (err) => setErrorMsg(err.message),
	});

	const { getAxiomData, isFetchingAxiom } = useGetAxiomData({
		onSuccess: () => setNotification("Axiom data loaded"),
		onError: (err) => setErrorMsg(err.message),
	});

	const handleGridReady = useCallback(
		(e: GridReadyEvent<MonthlyEstimation>) => {
			gridApiRef.current = e.api;
		},
		[],
	);

	const handleRowClicked = useCallback(
		(row: MonthlyEstimation) => setFilters({ month: row.Month }),
		[setFilters],
	);

	const handleAxiomFire = useCallback(
		(month: string) => {
			getAxiomData({ Month: month });
		},
		[getAxiomData],
	);

	const handleCloseDetail = useCallback(
		() => dropValues("month"),
		[dropValues],
	);

	const handleYearChange = useCallback(
		(newYear: number) => {
			setYear(newYear);
			// Replace, so Back leaves the page instead of stepping through every year looked at.
			// The month goes with the year: it belongs to the one being left.
			navigate(
				{ pathname: `${basePath}/${newYear}`, search: "" },
				{ replace: true },
			);
		},
		[basePath, navigate],
	);

	const handleSave = useCallback(
		(forecast: number | null, actual: number | null) => {
			if (!selectedRow) return;
			updateMonthlyData({
				Month: selectedRow.Month,
				ForecastedProduction: forecast,
				ActualProduction: actual,
			});
		},
		[selectedRow, updateMonthlyData],
	);

	useEffect(() => {
		if (dataQuery.error && !errorMsg) {
			setErrorMsg((dataQuery.error as Error).message);
		}
	}, [dataQuery.error, errorMsg]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Forecast Data Entry
			</Typography>
			<Paper elevation={3}>
				<ForecastDataEntryToolbar
					year={year}
					onYearChange={handleYearChange}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<Group
				id="forecast-data-entry-outer"
				orientation="horizontal"
				defaultLayout={outerLayout.defaultLayout ?? OUTER_LAYOUT}
				onLayoutChange={outerLayout.onLayoutChange}
				style={{ flex: 1, width: "100%", minHeight: 0 }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box sx={{ pr: 1, height: "100%" }}>
						<Group
							id="forecast-data-entry-inner"
							orientation="vertical"
							defaultLayout={innerLayout.defaultLayout ?? INNER_LAYOUT}
							onLayoutChange={innerLayout.onLayoutChange}
							style={{ height: "100%" }}
						>
							<Panel id="table" minSize={20}>
								<Paper
									elevation={3}
									sx={{ height: "100%", width: "100%", overflow: "hidden" }}
								>
									{dataQuery.isLoading ? (
										<CenteredLazyLoading />
									) : (
										<ForecastDataEntryTable
											rows={rows}
											selectedMonth={selectedMonth}
											isFetchingAxiom={isFetchingAxiom}
											onRowClicked={handleRowClicked}
											onAxiomFire={handleAxiomFire}
											onGridReady={handleGridReady}
										/>
									)}
								</Paper>
							</Panel>

							<Separator
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									cursor: "row-resize",
									padding: "2px 0",
								}}
							>
								<Box
									sx={{
										height: 6,
										width: 48,
										borderRadius: 1,
										backgroundColor: "#e0e0e0",
										display: "flex",
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "center",
										gap: "3px",
										transition: "background-color 0.2s",
										"&:hover": {
											backgroundColor: "primary.main",
											"& .grip-dot": { backgroundColor: "#fff" },
										},
									}}
								>
									{[...Array(5)].map((_, i) => (
										<Box
											key={`vgrip-dot-${i}`}
											className="grip-dot"
											sx={{
												width: 4,
												height: 4,
												borderRadius: "50%",
												backgroundColor: "#9e9e9e",
												transition: "background-color 0.2s",
											}}
										/>
									))}
								</Box>
							</Separator>

							<Panel id="detail" minSize={15}>
								<Paper
									elevation={3}
									sx={{
										height: "100%",
										overflowY: "auto",
										overflowX: "hidden",
									}}
								>
									<ForecastDetailViewer
										row={selectedRow}
										isSaving={isUpdating}
										onSave={handleSave}
										onClose={handleCloseDetail}
									/>
								</Paper>
							</Panel>
						</Group>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": { backgroundColor: "#fff" },
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={`hgrip-dot-${i}`}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper elevation={3} sx={{ height: "100%", p: 1 }}>
							<ForecastChart rows={rows} />
						</Paper>
					</Box>
				</Panel>
			</Group>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
